import { useCallback, useState } from "react";

import { toAlias } from "../../pages/CreateStackPage/wizard-helpers";
import {
  CloudProvider,
  DeployMode,
  type InfrastructureTarget,
  type InfraType,
  type ServiceConfig,
  type StackTemplate,
  type StackType,
} from "../../types";

const DEPLOY_MODES: {
  value: DeployMode;
  label: string;
  description: string;
}[] = [
  {
    value: DeployMode.RUSHA_MANAGED,
    label: "Rusha Managed",
    description: "Rusha builds and deploys",
  },
  {
    value: DeployMode.CI_BUILD_RUSHA_DEPLOY,
    label: "CI Build → Rusha Deploy",
    description: "Your CI builds, Rusha deploys",
  },
  {
    value: DeployMode.RUSHA_BUILD_CI_DEPLOY,
    label: "Rusha Build → CI Deploy",
    description: "Rusha builds, your CI deploys",
  },
  {
    value: DeployMode.CI_MANAGED,
    label: "CI Managed",
    description: "Your CI builds and deploys",
  },
];

const STACK_TYPES: { value: StackType; label: string; description: string }[] =
  [
    {
      value: "data_engineering" as StackType,
      label: "Data Engineering",
      description: "Ingest, process, store, and analyze data",
    },
    {
      value: "analytical" as StackType,
      label: "Analytical",
      description: "BI dashboards and reporting",
    },
    {
      value: "frontend_backend" as StackType,
      label: "Frontend + Backend",
      description: "Full-stack web application",
    },
  ];

export interface ApplicationFormState {
  name: string;
  stackType: StackType | null;
  cloudProvider: CloudProvider | null;
  infraType: InfraType | null;
  infraTarget: InfrastructureTarget | null;
  selectedTemplate: StackTemplate | null;
  configuredServices: ServiceConfig[];
  deployMode: DeployMode;
  region: string;
  enableObservability: boolean;
}

export const INITIAL_APP_STATE: ApplicationFormState = {
  name: "",
  stackType: null,
  cloudProvider: CloudProvider.RUSHA_HOSTED,
  infraType: "rusha_hosted" as InfraType,
  infraTarget: null,
  selectedTemplate: null,
  configuredServices: [],
  deployMode: DeployMode.RUSHA_MANAGED,
  region: "",
  enableObservability: true,
};

export interface ApplicationFormErrors {
  name?: string;
  stackType?: string;
  region?: string;
}

const NAME_RE = /^[a-zA-Z][a-zA-Z0-9_-]*$/;

export function useApplicationForm() {
  const [state, setState] = useState<ApplicationFormState>(INITIAL_APP_STATE);
  const [errors, setErrors] = useState<ApplicationFormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const validateField = useCallback((field: string, value: string) => {
    if (field === "name") {
      if (!value.trim()) return "Application name is required";
      if (!NAME_RE.test(value))
        return "Must start with a letter, only alphanumeric, hyphens, and underscores";
      if (value.length > 63) return "Name must be 63 characters or less";
    }
    if (field === "stackType") {
      if (!value) return "Select a domain type";
    }
    if (field === "region") {
      if (!value.trim()) return "Region is required";
    }
    return undefined;
  }, []);

  const setField = useCallback(
    <K extends keyof ApplicationFormState>(
      field: K,
      value: ApplicationFormState[K],
    ) => {
      setState((s) => ({ ...s, [field]: value }));
      if (touched[field as string]) {
        const err = validateField(field as string, String(value));
        setErrors((e) => ({ ...e, [field as string]: err }));
      }
    },
    [touched, validateField],
  );

  const onBlur = useCallback(
    (field: string) => {
      setTouched((t) => ({ ...t, [field]: true }));
      const err = validateField(
        field,
        String(state[field as keyof ApplicationFormState] ?? ""),
      );
      setErrors((e) => ({ ...e, [field]: err }));
    },
    [state, validateField],
  );

  const validate = useCallback((): boolean => {
    const newErrors: ApplicationFormErrors = {};
    const nameErr = validateField("name", state.name);
    if (nameErr) newErrors.name = nameErr;
    const typeErr = validateField("stackType", String(state.stackType ?? ""));
    if (typeErr) newErrors.stackType = typeErr;
    const regionErr = validateField("region", state.region);
    if (regionErr) newErrors.region = regionErr;
    setErrors(newErrors);
    setTouched({ name: true, stackType: true, region: true });
    return Object.keys(newErrors).length === 0;
  }, [state, validateField]);

  const buildPayload = useCallback(
    (projectAlias: string) => {
      if (
        !state.stackType ||
        !state.cloudProvider ||
        !state.infraType ||
        !state.selectedTemplate
      ) {
        return null;
      }
      return {
        name: state.name.trim(),
        alias: toAlias(state.name),
        projectAlias,
        systemType: state.stackType,
        cloudProvider: state.cloudProvider,
        infraType: state.infraType,
        targetId: state.infraTarget?.id,
        templateSlug: state.selectedTemplate.slug,
        stackConfig: {
          ...(state.selectedTemplate.stackConfig ?? {}),
          ...(state.enableObservability ? { _observability: true } : {}),
        },
        services:
          state.configuredServices.length > 0
            ? state.configuredServices
            : undefined,
        region: state.region,
        deployMode: state.deployMode,
        status: "draft" as const,
      };
    },
    [state],
  );

  return {
    state,
    errors,
    setField,
    onBlur,
    validate,
    buildPayload,
    DEPLOY_MODES,
    STACK_TYPES,
  };
}
